/**
 * POS Firebase Auth Service
 *
 * Spezialisierter Auth Service für das POS-System mit strikten Zugriffsbeschränkungen.
 *
 * WICHTIG: Nur User mit POS-Rollen können sich anmelden!
 * - pos_admin: Voller Zugriff auf alle Locations
 * - pos_manager: Zugriff auf zugewiesene Locations, kann Z-Reports erstellen
 * - pos_cashier: Zugriff auf zugewiesene Location, kann kassieren
 * - pos_seller: Basis-Zugriff auf zugewiesene Location
 *
 * Location-Binding:
 * - Jeder User (außer pos_admin) MUSS eine primaryLocationId haben
 * - User kann nur auf Locations zugreifen die in allowedLocationIds stehen
 *
 * @author TypeScript Developer
 * @version 2.1.0 - Added profile update methods
 */

import { Injectable, inject, EnvironmentInjector, runInInjectionContext } from '@angular/core';
import {
  Auth,
  User,
  UserCredential,
  authState,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signOut as firebaseSignOut,
  getIdTokenResult,
  updateProfile,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from '@angular/fire/auth';
import { Database, ref, onValue, off } from '@angular/fire/database';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { NotificationService } from './notification.service';
import { TranslateService } from '@ngx-translate/core';
import { TenantContextService } from '@pos-shared';

/**
 * Erlaubte POS-Rollen
 */
export const POS_ROLES = ['pos_admin', 'pos_manager', 'pos_cashier', 'pos_seller'] as const;
export type PosRole = typeof POS_ROLES[number];

/**
 * Leitet POS-Rolle aus Tenant-Rolle ab (Single Source of Truth)
 * role → posRole Mapping
 */
function derivePosRole(role: string | null): PosRole | null {
  if (!role) return null;
  const mapping: Record<string, PosRole> = {
    'owner': 'pos_admin',
    'admin': 'pos_admin',
    'manager': 'pos_manager',
    'cashier': 'pos_cashier',
    'seller': 'pos_seller',
    'viewer': 'pos_seller'
  };
  return mapping[role] || null;
}

/**
 * Custom Error für POS Auth
 */
export class PosAuthError extends Error {
  constructor(
    message: string,
    public code: string
  ) {
    super(message);
    this.name = 'PosAuthError';
  }
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  // Dependency Injection
  private auth = inject(Auth);
  private database = inject(Database);
  private notificationService = inject(NotificationService);
  private translate = inject(TranslateService);
  private tenantContext = inject(TenantContextService);
  private injectionContext = inject(EnvironmentInjector);

  // Reactive State Management
  private readonly _user$ = authState(this.auth);
  private readonly _currentUserSubject = new BehaviorSubject<User | null>(null);

  /**
   * Observable für den aktuellen User
   */
  readonly user$: Observable<User | null> = this._user$.pipe(
    tap(user => {
      this._currentUserSubject.next(user);
    })
  );

  /**
   * Observable für Authentication Status
   */
  readonly isAuthenticated$: Observable<boolean> = this.user$.pipe(
    map(user => !!user)
  );

  /**
   * BehaviorSubject für User-Rollen aus Custom Claims
   */
  private readonly currentRolesSubject = new BehaviorSubject<string[]>([]);

  /**
   * Observable für User-Rollen
   */
  public readonly roles$: Observable<string[]> = this.currentRolesSubject.asObservable();

  /**
   * BehaviorSubject für POS-Rolle (die höchste POS-Rolle des Users)
   */
  private readonly posRoleSubject = new BehaviorSubject<PosRole | null>(null);

  /**
   * Observable für die aktive POS-Rolle
   */
  public readonly posRole$: Observable<PosRole | null> = this.posRoleSubject.asObservable();

  /**
   * BehaviorSubject für Auth Initialization Status
   */
  private readonly authInitializedSubject = new BehaviorSubject<boolean>(false);

  /**
   * Observable für Auth Initialization Status
   * Wird true wenn User-Claims vollständig geladen sind
   */
  public readonly authInitialized$: Observable<boolean> = this.authInitializedSubject.asObservable();

  // === POS LOCATION BINDING ===

  /**
   * BehaviorSubject für primäre Location ID aus Custom Claims
   */
  private readonly primaryLocationIdSubject = new BehaviorSubject<string | null>(null);

  /**
   * Observable für primäre Location ID
   */
  public readonly primaryLocationId$: Observable<string | null> = this.primaryLocationIdSubject.asObservable();

  /**
   * BehaviorSubject für erlaubte Location IDs aus Custom Claims
   */
  private readonly allowedLocationIdsSubject = new BehaviorSubject<string[]>([]);

  /**
   * Observable für erlaubte Location IDs
   */
  public readonly allowedLocationIds$: Observable<string[]> = this.allowedLocationIdsSubject.asObservable();

  /**
   * Synchroner Zugriff auf den aktuellen User
   */
  get currentUser(): User | null {
    return this._currentUserSubject.value;
  }

  /**
   * User UID
   */
  get currentUserId(): string | null {
    return this.currentUser?.uid ?? null;
  }

  /**
   * Prüft ob User eingeloggt ist
   */
  get isAuthenticated(): boolean {
    return !!this.currentUser;
  }

  /**
   * Gibt die aktuelle POS-Rolle zurück
   */
  get currentPosRole(): PosRole | null {
    return this.posRoleSubject.value;
  }

  /**
   * Prüft ob User ein POS Admin ist
   */
  get isPosAdmin(): boolean {
    return this.posRoleSubject.value === 'pos_admin';
  }

  /**
   * Prüft ob User ein POS Manager ist
   */
  get isPosManager(): boolean {
    return this.posRoleSubject.value === 'pos_manager' || this.isPosAdmin;
  }

  constructor() {
    // Subscribe auf User Changes um Rollen & Permissions automatisch zu laden
    this.user$.subscribe(async (firebaseUser) => {
      if (firebaseUser) {
        // User eingeloggt - Custom Claims laden
        await this.loadUserClaims(firebaseUser);
      } else {
        // User ausgeloggt - Alles zurücksetzen
        this.resetState();
      }
    });
  }

  /**
   * Setzt alle State-Subjects zurück
   */
  private resetState(): void {
    this.currentRolesSubject.next([]);
    this.posRoleSubject.next(null);
    this.primaryLocationIdSubject.next(null);
    this.allowedLocationIdsSubject.next([]);
    this.authInitializedSubject.next(false);
    this.tenantContext.clearContext();
  }

  // ==================== CORE AUTH METHODS ====================

  /**
   * Meldet einen User mit E-Mail und Passwort an.
   *
   * WICHTIG: Prüft ob User eine POS-Rolle hat!
   * Wenn nicht, wird der Login abgelehnt und der User automatisch ausgeloggt.
   *
   * @throws PosAuthError wenn User keine POS-Rolle hat
   * @throws PosAuthError wenn User keine Location zugewiesen hat (außer pos_admin)
   */
  async signIn(email: string, password: string): Promise<UserCredential> {
    try {
      // 1. Firebase Login
      const userCredential = await signInWithEmailAndPassword(
        this.auth,
        email,
        password
      );

      // 2. Custom Claims laden und prüfen
      const claims = await this.loadAndValidateClaims(userCredential.user);

      // 3. Prüfe POS-Rolle
      const posRole = claims.posRole;
      if (!posRole) {
        // User hat keine POS-Rolle - ausloggen und Fehler werfen
        await firebaseSignOut(this.auth);
        throw new PosAuthError(
          'Zugriff verweigert: Sie haben keine Berechtigung für das POS-System.',
          'pos/no-pos-role'
        );
      }

      // 4. Prüfe Location-Binding (außer für pos_admin)
      if (posRole !== 'pos_admin') {
        const hasLocation = claims.primaryLocationId || (claims.allowedLocationIds && claims.allowedLocationIds.length > 0);
        if (!hasLocation) {
          await firebaseSignOut(this.auth);
          throw new PosAuthError(
            'Zugriff verweigert: Ihnen wurde keine Verkaufsstelle zugewiesen.',
            'pos/no-location-assigned'
          );
        }
      }

      // 5. State aktualisieren (posRole + CRM role für vollständige Berechtigungen)
      const allRoles: string[] = posRole ? [posRole] : [];
      if (claims.role && !allRoles.includes(claims.role)) {
        allRoles.push(claims.role);
      }
      this.currentRolesSubject.next(allRoles);
      this.posRoleSubject.next(posRole);
      this.primaryLocationIdSubject.next(claims.primaryLocationId);
      this.allowedLocationIdsSubject.next(claims.allowedLocationIds);

      // 6. TenantContext SOFORT setzen (synchron, BEVOR Router navigiert!)
      // FIX: Race Condition - Guards laufen sonst bevor TenantContext gesetzt ist
      if (claims.tenantId) {
        // Location aus Claims ODER bestehenden Context/localStorage beibehalten
        const existingLocationId = this.tenantContext.locationId();
        const contextUpdate: any = {
          tenantId: claims.tenantId,
          tenantName: null,
          role: this.mapRoleToTenantRole(claims.role),
          permissions: this.getRolePermissions(claims.role || 'viewer'),
          locationIds: claims.allowedLocationIds
        };
        // Nur Location überschreiben wenn Claims eine primaryLocationId haben
        if (claims.primaryLocationId) {
          contextUpdate.locationId = claims.primaryLocationId;
          contextUpdate.locationName = null;
        } else if (!existingLocationId) {
          // Kein Claim und kein bestehender Context → aus localStorage laden
          this.tenantContext.loadFromStorage();
        }
        this.tenantContext.setContext(contextUpdate);
        console.log('[POS Auth] TenantContext synchron gesetzt:', claims.tenantId);
      }

      console.log('[POS Auth] Login erfolgreich:', {
        email: userCredential.user.email,
        posRole,
        primaryLocationId: claims.primaryLocationId,
        allowedLocations: claims.allowedLocationIds.length
      });

      this.notificationService.showSuccess(
        'Willkommen zurück!',
        'Login erfolgreich'
      );

      return userCredential;
    } catch (error: any) {
      // PosAuthError direkt weiterwerfen
      if (error instanceof PosAuthError) {
        this.notificationService.showError(error.message);
        throw error;
      }

      // Firebase Errors behandeln
      this.handleAuthError(error, 'Login fehlgeschlagen');
      throw error;
    }
  }

  /**
   * Meldet den aktuellen User ab
   */
  async signOut(): Promise<void> {
    try {
      await firebaseSignOut(this.auth);
      this.resetState();

      this.notificationService.showInfo(
        'Auf Wiedersehen!',
        'Logout erfolgreich'
      );
    } catch (error: any) {
      this.handleAuthError(error, 'Logout fehlgeschlagen');
      throw error;
    }
  }

  // ==================== REGISTRATION METHODS ====================

  /**
   * Registriert einen neuen POS-User mit E-Mail und Passwort.
   *
   * WICHTIG: User muss vorher in userInvites eingeladen worden sein!
   * Nach Registrierung wird Verification-Email gesendet.
   *
   * @param email - E-Mail-Adresse (muss mit Invite übereinstimmen)
   * @param password - Gewähltes Passwort
   * @param displayName - Anzeigename des Users
   * @returns UserCredential des neuen Users
   */
  async register(email: string, password: string, displayName: string): Promise<UserCredential> {
    try {
      // 1. Firebase User erstellen
      const userCredential = await createUserWithEmailAndPassword(
        this.auth,
        email,
        password
      );

      // 2. Display Name setzen
      if (displayName) {
        await updateProfile(userCredential.user, { displayName: displayName.trim() });
      }

      // 3. Verification Email senden
      await sendEmailVerification(userCredential.user);

      console.log('[POS Auth] Registrierung erfolgreich:', {
        email: userCredential.user.email,
        displayName: displayName
      });

      return userCredential;
    } catch (error: any) {
      console.error('[POS Auth] Registrierung fehlgeschlagen:', error);
      this.handleAuthError(error, 'Registrierung fehlgeschlagen');
      throw error;
    }
  }

  /**
   * Prüft ob die Email des aktuellen Users verifiziert ist
   */
  get isEmailVerified(): boolean {
    return this.currentUser?.emailVerified ?? false;
  }

  /**
   * Observable für Email-Verification Status
   */
  readonly isEmailVerified$: Observable<boolean> = this.user$.pipe(
    map(user => user?.emailVerified ?? false)
  );

  /**
   * Sendet die Verification-Email erneut
   */
  async resendVerificationEmail(): Promise<void> {
    const user = this.currentUser;
    if (!user) {
      throw new PosAuthError('Kein User eingeloggt', 'auth/no-user');
    }

    if (user.emailVerified) {
      throw new PosAuthError('Email bereits verifiziert', 'auth/already-verified');
    }

    try {
      await sendEmailVerification(user);
      console.log('[POS Auth] Verification Email erneut gesendet');
      this.notificationService.showSuccess('Bestätigungs-Email wurde erneut gesendet');
    } catch (error: any) {
      console.error('[POS Auth] Fehler beim Senden der Verification Email:', error);
      throw error;
    }
  }

  /**
   * Lädt die User-Daten neu und prüft emailVerified Status
   * Wird nach Email-Verification aufgerufen
   */
  async reloadUser(): Promise<boolean> {
    const user = this.currentUser;
    if (!user) return false;

    try {
      await user.reload();
      // Neues Token holen um emailVerified im Token zu aktualisieren
      await user.getIdToken(true);

      // User-Subject aktualisieren
      this._currentUserSubject.next(this.auth.currentUser);

      console.log('[POS Auth] User neu geladen, emailVerified:', this.auth.currentUser?.emailVerified);
      return this.auth.currentUser?.emailVerified ?? false;
    } catch (error) {
      console.error('[POS Auth] Fehler beim Neuladen des Users:', error);
      return false;
    }
  }

  // ==================== RTDB LISTENER FOR EMAIL VERIFICATION ====================

  private refreshTimeUnsubscribe: (() => void) | null = null;

  /**
   * Startet einen Listener auf die RTDB für Token-Refresh Signale.
   * Wird von der VerifyEmailPending-Seite verwendet.
   *
   * Wenn das CRM den refreshTime-Wert aktualisiert (nach Email-Verification),
   * wird automatisch user.reload() aufgerufen um den neuen emailVerified Status zu holen.
   *
   * @param callback - Wird aufgerufen wenn emailVerified true wird
   */
  startEmailVerificationListener(callback: (verified: boolean) => void): void {
    const user = this.currentUser;
    if (!user) return;

    // Pfad: /metadata/{uid}/refreshTime
    const refreshTimeRef = ref(this.database, `metadata/${user.uid}/refreshTime`);

    // Listener starten
    const unsubscribe = onValue(refreshTimeRef, async (snapshot) => {
      if (snapshot.exists()) {
        console.log('[POS Auth] RefreshTime Signal empfangen:', snapshot.val());

        // User-Daten neu laden
        const isVerified = await this.reloadUser();

        if (isVerified) {
          console.log('[POS Auth] Email verifiziert!');
          callback(true);
        }
      }
    });

    // Unsubscribe-Funktion speichern
    this.refreshTimeUnsubscribe = () => off(refreshTimeRef);

    console.log('[POS Auth] Email-Verification Listener gestartet für:', user.uid);
  }

  /**
   * Stoppt den RTDB Listener für Email-Verification
   */
  stopEmailVerificationListener(): void {
    if (this.refreshTimeUnsubscribe) {
      this.refreshTimeUnsubscribe();
      this.refreshTimeUnsubscribe = null;
      console.log('[POS Auth] Email-Verification Listener gestoppt');
    }
  }

  // ==================== PROFILE UPDATE METHODS ====================

  /**
   * Aktualisiert den Display Name des Users
   *
   * @param displayName - Der neue Display Name
   * @throws Error wenn kein User eingeloggt ist
   */
  async updateUserProfile(displayName: string): Promise<void> {
    const user = this.currentUser;
    if (!user) {
      throw new PosAuthError('Kein User eingeloggt', 'auth/no-user');
    }

    try {
      await updateProfile(user, { displayName: displayName.trim() });
      console.log('[POS Auth] Display Name aktualisiert:', displayName);

      // User-Subject aktualisieren (trigger UI updates)
      this._currentUserSubject.next(user);
    } catch (error: any) {
      console.error('[POS Auth] Fehler beim Aktualisieren des Profils:', error);
      throw error;
    }
  }

  /**
   * Aendert das Passwort des Users
   *
   * WICHTIG: Erfordert Re-Authentifizierung mit aktuellem Passwort!
   *
   * @param currentPassword - Das aktuelle Passwort zur Verifikation
   * @param newPassword - Das neue Passwort
   * @throws Error wenn Re-Authentifizierung fehlschlaegt
   * @throws Error wenn Passwort-Aenderung fehlschlaegt
   */
  async updateUserPassword(currentPassword: string, newPassword: string): Promise<void> {
    const user = this.currentUser;
    if (!user || !user.email) {
      throw new PosAuthError('Kein User eingeloggt', 'auth/no-user');
    }

    try {
      // 1. Re-Authentifizierung mit aktuellem Passwort
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      console.log('[POS Auth] Re-Authentifizierung erfolgreich');

      // 2. Passwort aendern
      await updatePassword(user, newPassword);
      console.log('[POS Auth] Passwort erfolgreich geaendert');
    } catch (error: any) {
      console.error('[POS Auth] Fehler beim Aendern des Passworts:', error);
      throw error;
    }
  }

  // ==================== CLAIMS METHODS ====================

  /**
   * Lädt und validiert Custom Claims
   */
  private async loadAndValidateClaims(user: User): Promise<{
    tenantId: string | null;
    role: string | null;
    posRole: PosRole | null;
    primaryLocationId: string | null;
    allowedLocationIds: string[];
  }> {
    return runInInjectionContext(this.injectionContext, async () => {
      const idTokenResult = await getIdTokenResult(user);
      const customClaims = idTokenResult.claims;

      // Tenant Claims extrahieren
      const tenantId = (customClaims['tenantId'] as string) || null;
      const role = (customClaims['role'] as string) || null;

      // POS-Rolle aus Tenant-Rolle ableiten (Single Source of Truth)
      const posRole = derivePosRole(role);

      // Location Claims extrahieren
      const primaryLocationId = (customClaims['primaryLocationId'] as string) || null;
      const allowedLocationIds: string[] =
        (Array.isArray(customClaims['allowedLocationIds']) ? customClaims['allowedLocationIds'] as string[] : []);

      return { tenantId, role, posRole, primaryLocationId, allowedLocationIds };
    });
  }

  /**
   * Lädt User Claims bei Auth State Change
   */
  private async loadUserClaims(firebaseUser: User): Promise<void> {
    try {
      const claims = await this.loadAndValidateClaims(firebaseUser);

      // POS-Rolle aus Claims
      const posRole = claims.posRole;

      // State aktualisieren (posRole + CRM role für vollständige Berechtigungen)
      const allRoles: string[] = posRole ? [posRole] : [];
      if (claims.role && !allRoles.includes(claims.role)) {
        allRoles.push(claims.role);
      }
      this.currentRolesSubject.next(allRoles);
      this.posRoleSubject.next(posRole);
      this.primaryLocationIdSubject.next(claims.primaryLocationId);
      this.allowedLocationIdsSubject.next(claims.allowedLocationIds);

      // TenantContext setzen - bestehende Location beibehalten wenn keine primaryLocationId in Claims
      if (claims.tenantId) {
        const existingLocationId = this.tenantContext.locationId();
        const contextUpdate: any = {
          tenantId: claims.tenantId,
          tenantName: null,
          role: this.mapRoleToTenantRole(claims.role),
          permissions: this.getRolePermissions(claims.role || 'viewer'),
          locationIds: claims.allowedLocationIds
        };
        if (claims.primaryLocationId) {
          contextUpdate.locationId = claims.primaryLocationId;
          contextUpdate.locationName = null;
        } else if (!existingLocationId) {
          this.tenantContext.loadFromStorage();
        }
        this.tenantContext.setContext(contextUpdate);
      }

      console.log('[POS AuthService] Claims geladen:', {
        role: claims.role,
        posRole,
        tenantId: claims.tenantId || 'nicht gesetzt',
        primaryLocationId: claims.primaryLocationId || 'nicht gesetzt',
        allowedLocationCount: claims.allowedLocationIds.length
      });

      // Auth vollständig initialisiert
      this.authInitializedSubject.next(true);
    } catch (error) {
      console.error('[POS AuthService] Fehler beim Laden der Claims:', error);
      this.resetState();
      // Auch bei Fehler als initialisiert markieren (damit Guards nicht hängen bleiben)
      this.authInitializedSubject.next(true);
    }
  }

  /**
   * Mappt Custom Claims Role zu TenantContext Role
   */
  private mapRoleToTenantRole(role: string | null): 'owner' | 'admin' | 'manager' | 'cashier' | 'viewer' | null {
    if (!role) return null;

    // Direct mapping
    const roleMap: Record<string, 'owner' | 'admin' | 'manager' | 'cashier' | 'viewer'> = {
      'owner': 'owner',
      'admin': 'admin',
      'manager': 'manager',
      'cashier': 'cashier',
      'viewer': 'viewer',
      // POS-spezifische Rollen mapping
      'pos_admin': 'admin',
      'pos_manager': 'manager',
      'pos_cashier': 'cashier',
      'pos_seller': 'cashier'
    };

    return roleMap[role] || 'viewer';
  }

  /**
   * Gibt Permissions basierend auf Rolle zurück
   */
  private getRolePermissions(role: string): string[] {
    const permissions: Record<string, string[]> = {
      'owner': ['all'],
      'admin': ['all'],
      'pos_admin': ['all'],
      'manager': ['products.read', 'products.write', 'transactions.read', 'inventory.read', 'inventory.write'],
      'pos_manager': ['products.read', 'products.write', 'transactions.read', 'inventory.read', 'inventory.write'],
      'cashier': ['transactions.create', 'products.read'],
      'pos_cashier': ['transactions.create', 'products.read'],
      'pos_seller': ['transactions.create', 'products.read'],
      'viewer': ['products.read', 'transactions.read']
    };
    return permissions[role] || permissions['viewer'];
  }

  // ==================== ROLE & PERMISSION CHECKS ====================

  /**
   * Gibt die aktuellen Rollen synchron zurück
   */
  getCurrentRoles(): string[] {
    return this.currentRolesSubject.value;
  }

  /**
   * Prüft ob User eine bestimmte POS-Rolle hat
   */
  hasPosRole(role: PosRole): boolean {
    const currentRole = this.posRoleSubject.value;
    if (!currentRole) return false;

    // Hierarchie: admin > manager > cashier > seller
    const roleHierarchy: Record<PosRole, number> = {
      'pos_admin': 4,
      'pos_manager': 3,
      'pos_cashier': 2,
      'pos_seller': 1
    };

    return roleHierarchy[currentRole] >= roleHierarchy[role];
  }

  /**
   * Prüft ob User eine bestimmte Rolle hat (generisch)
   */
  hasRole(role: string): boolean {
    return this.currentRolesSubject.value.includes(role);
  }

  // ==================== LOCATION METHODS ====================

  /**
   * Gibt die primäre Location ID synchron zurück
   */
  getPrimaryLocationId(): string | null {
    return this.primaryLocationIdSubject.value;
  }

  /**
   * Gibt alle erlaubten Location IDs synchron zurück
   */
  getAllowedLocationIds(): string[] {
    return this.allowedLocationIdsSubject.value;
  }

  /**
   * Prüft ob User eine bestimmte Location nutzen darf
   *
   * - pos_admin: Zugriff auf ALLE Locations
   * - Andere: Nur auf Locations in allowedLocationIds
   */
  canAccessLocation(locationId: string): boolean {
    // pos_admin hat Zugriff auf alle Locations
    if (this.isPosAdmin) {
      return true;
    }

    const allowedIds = this.allowedLocationIdsSubject.value;
    const primaryId = this.primaryLocationIdSubject.value;

    // Prüfe ob Location erlaubt ist
    if (allowedIds.length > 0) {
      return allowedIds.includes(locationId);
    }

    // Fallback: Nur primäre Location erlaubt
    return locationId === primaryId;
  }

  /**
   * Gibt die Standard-Location für den User zurück
   * (primaryLocationId oder erste aus allowedLocationIds)
   */
  getDefaultLocationId(): string | null {
    const primaryId = this.primaryLocationIdSubject.value;
    if (primaryId) return primaryId;

    const allowedIds = this.allowedLocationIdsSubject.value;
    if (allowedIds.length > 0) return allowedIds[0];

    return null;
  }

  // ==================== UTILITY METHODS ====================

  /**
   * Wartet bis Firebase Auth vollständig initialisiert ist
   */
  async waitForAuthInit(): Promise<User | null> {
    return new Promise((resolve) => {
      if (this.auth.currentUser !== undefined) {
        resolve(this.auth.currentUser);
        return;
      }

      let firstEmission = true;
      const unsubscribe = this.auth.onAuthStateChanged((user) => {
        if (firstEmission) {
          firstEmission = false;
          if (user !== null) {
            unsubscribe();
            resolve(user);
          }
        } else {
          unsubscribe();
          resolve(user);
        }
      });
    });
  }

  /**
   * Erzwingt das Neuladen der Custom Claims
   * (z.B. nach Rollen-Änderung durch Admin)
   */
  async refreshClaims(): Promise<void> {
    const user = this.currentUser;
    if (!user) return;

    // Token neu laden erzwingen
    await user.getIdToken(true);

    // Claims neu laden
    await this.loadUserClaims(user);
  }

  // ==================== ERROR HANDLING ====================

  /**
   * Zentrales Error Handling für Auth-Operationen
   */
  private handleAuthError(error: any, defaultMessage: string): void {
    console.error('[POS Auth Error]:', error);

    let message = defaultMessage;
    const errorCode = error?.code || '';

    // Firebase Error Code Mapping
    switch (errorCode) {
      case 'auth/invalid-email':
        message = 'Ungültige E-Mail-Adresse';
        break;
      case 'auth/user-not-found':
        message = 'Benutzer nicht gefunden';
        break;
      case 'auth/wrong-password':
        message = 'Falsches Passwort';
        break;
      case 'auth/invalid-credential':
        message = 'Ungültige Anmeldedaten';
        break;
      case 'auth/user-disabled':
        message = 'Benutzer wurde deaktiviert';
        break;
      case 'auth/too-many-requests':
        message = 'Zu viele Anmeldeversuche. Bitte später erneut versuchen.';
        break;
      case 'auth/network-request-failed':
        message = 'Netzwerkfehler. Bitte Internetverbindung prüfen.';
        break;
    }

    this.notificationService.showError(message);
  }
}
